import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@this/src/components/shared/ui/data_displays/table';
import moment from 'moment';
import { Fetcher } from '../../../../util/fetcher/fetcher.util';

const messageContainerStyle = {
  textAlign: 'center' as const,
  padding: '20px'
};

const messages = {
  loading: 'ログを読み込み中...',
  noLogs: {
    main: 'このTrace IDに関連するAI Operatorのステップログはまだ記録されていません。',
    sub: 'AI Operatorで予約操作を行うと、自動的にステップログが記録されます。'
  }
} as const;

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  started: { label: 'started', color: '#1976d2' },
  success: { label: 'success', color: '#2e7d32' },
  failure: { label: 'failure', color: '#d32f2f' }
};

interface ExtensionStepLog {
  step_name: string;
  status: string;
  detail: string | null;
  executed_at: string;
}

interface ExtensionStepLogsProps {
  traceId: string;
}

interface ExtensionStepLogsResponse {
  logs: ExtensionStepLog[];
}

const fetchExtensionStepLogs = async (traceId: string): Promise<ExtensionStepLogsResponse> => {
  return Fetcher.get(`/arrangement/order_item/${traceId}/extension_step_logs`);
};

export const ExtensionStepLogs: React.FC<ExtensionStepLogsProps> = ({ traceId }) => {
  const [logs, setLogs] = useState<ExtensionStepLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLogs = async () => {
      try {
        const response = await fetchExtensionStepLogs(traceId);
        setLogs(response.logs);
      } catch (error) {
        console.error('Failed to load extension step logs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLogs();
  }, [traceId]);

  if (loading) {
    return (
      <div style={messageContainerStyle}>
        <p>{messages.loading}</p>
      </div>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <div style={messageContainerStyle}>
        <p>{messages.noLogs.main}</p>
        <p>{messages.noLogs.sub}</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>実行日時</TableCell>
          <TableCell>ステップ名</TableCell>
          <TableCell>ステータス</TableCell>
          <TableCell>詳細</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {logs.map(log => {
          const statusInfo = STATUS_LABELS[log.status] || { label: log.status, color: '#666' };
          return (
            <TableRow key={`${log.executed_at}-${log.step_name}-${log.status}`}>
              <TableCell nowrap>{moment(log.executed_at).format('YYYY-MM-DD HH:mm:ss')}</TableCell>
              <TableCell>{log.step_name}</TableCell>
              <TableCell>
                <span style={{ color: statusInfo.color, fontWeight: 'bold' }}>{statusInfo.label}</span>
              </TableCell>
              <TableCell>{log.detail}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
